import { stringLength } from 'utils/format'

import {
  ACCEPT_FILE_EXT,
  ACCEPT_FILE_EXT_IMG_ONLY,
  MAX_FILENAME_LENGTH,
  FILE_SIZE_LIMITS,
  FILE_SIZE_ERROR_MESSAGES,
  FILE_SIZE_ERROR_IMG_EXT_ONLY,
  IMG_FILE_EXT_ERROR_MSG,
  FILE_EXT_ERROR_MSG,
  FILENAME_LENGTH_ERROR_MSG,
  FILE_SIZE_LIMIT_20,
} from '../config'

const getErrorMessage = (extension: string, isImagesOnly: boolean) => {
  return isImagesOnly
    ? FILE_SIZE_ERROR_IMG_EXT_ONLY
    : FILE_SIZE_ERROR_MESSAGES[extension]
}
const fileSizeValidator = (
  extension: string,
  fileSizeMib: number,
  isImagesOnly: boolean,
) => {
  const lowerExtension = extension.toLowerCase()
  const limit = isImagesOnly
    ? FILE_SIZE_LIMIT_20
    : FILE_SIZE_LIMITS[lowerExtension]
  if (limit !== undefined && fileSizeMib >= limit) {
    return getErrorMessage(lowerExtension, isImagesOnly)
  }
}

function validFileExt(ext: string, extensions: readonly string[]) {
  return extensions.includes(ext.toLowerCase())
}

const desktopFileExtValidator = (ext: string) => {
  if (!validFileExt(ext, ACCEPT_FILE_EXT)) {
    return FILE_EXT_ERROR_MSG
  }
}

const imgOnlyFileExtValidator = (ext: string) => {
  if (!validFileExt(ext, ACCEPT_FILE_EXT_IMG_ONLY)) {
    return IMG_FILE_EXT_ERROR_MSG
  }
}

const filenameLengthValidator = (filename: string) => {
  if (stringLength(filename) >= MAX_FILENAME_LENGTH) {
    return FILENAME_LENGTH_ERROR_MSG
  }
}

export function nameWithOutExt(filename: string) {
  const matched = filename.match(/(.+)(\..+)$/)
  if (matched) {
    const name = matched[1]
    if (!name) {
      throw new Error('Unexpected name error')
    }
    return name
  }

  return filename
}

// type example: 'application/zip' 'text/csv'
export function downloadFile(filename: string, type: string, data: any) {
  const url = URL.createObjectURL(new Blob([data], { type }))
  const element = document.createElement('a')
  element.href = url
  element.setAttribute('download', filename)

  element.style.display = 'none'
  document.body.appendChild(element)
  element.click()

  document.body.removeChild(element)
  URL.revokeObjectURL(url)
}

export function checkFilesErrors(
  files: File[],
  isImagesOnly = false,
): string[] {
  const errMsg: string[] = []
  files.forEach((f) => {
    errMsg.push(...checkFileError(f, isImagesOnly))
  })
  return errMsg.filter((m, i, self) => self.indexOf(m) === i) // unique
}

export function checkFileError(file: File, isImagesOnly = false): string[] {
  const errMsg: (string | undefined)[] = []
  const ext = file.name.split('.').pop() || ''
  const fileSizeMib = file.size / 1024 ** 2
  errMsg.push(fileSizeValidator(ext, fileSizeMib, isImagesOnly))
  errMsg.push(filenameLengthValidator(file.name))
  errMsg.push(
    isImagesOnly ? imgOnlyFileExtValidator(ext) : desktopFileExtValidator(ext),
  )
  return errMsg.filter((m): m is string => m !== undefined)
}

export async function readFileTextContent(file: File): Promise<string> {
  if (typeof file.text === 'function') {
    return file.text()
  } else {
    return readWithFileReader(file)
  }
}

function readWithFileReader(file: File): Promise<string> {
  const fileReader = new FileReader()

  return new Promise((resolve, reject) => {
    fileReader.readAsText(file)
    fileReader.onload = function () {
      resolve(fileReader.result as string)
    }
    fileReader.onerror = function () {
      reject(fileReader.error)
    }
  })
}
